import { useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { GameKbArtifactMeta, GameKbStage } from '@sr-test-wizard/shared';
import { useGameKbStore, type GameKbStageProgress } from '../../state/gameKbStore';
import { gameKbApi } from '../../lib/gameKbApi';
import { GameChat } from './GameChat';
import { MarkdownView, ReportView } from './ReportView';

/**
 * Opt-in "post the generated Game Description to the run's TEXEC as a Jira
 * comment" button. Mirrors the reference app's two-step flow: generate,
 * review, then explicitly post.
 */
function PostToJiraButton({ runId, disabled }: { runId: string; disabled: boolean }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ texecKey: string; url: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const post = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await gameKbApi.postDescriptionComment(runId);
      setResult({ texecKey: data.texecKey, url: data.url });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="row" style={{ alignItems: 'center', gap: 8 }}>
      <button type="button" className="btn" disabled={disabled || busy} onClick={() => void post()}>
        {busy ? 'Posting…' : 'Post to Jira'}
      </button>
      {result && (
        <span className="small good">
          Posted to {result.url ? <a href={result.url} target="_blank" rel="noreferrer">{result.texecKey}</a> : result.texecKey}
        </span>
      )}
      {error && <span className="small" style={{ color: 'var(--bad)' }}>{error}</span>}
    </div>
  );
}

/**
 * Game KB as the catalogue's proposer: an agentic pass reads this game and
 * drafts new concepts / brandings into the ontology as `draft` for a human to
 * approve on the Ontology page. This is the on-ramp that grows the catalogue
 * from real games instead of hand-authoring every concept.
 */
function ProposeConceptsCard({ runId }: { runId: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Awaited<ReturnType<typeof gameKbApi.proposeConcepts>> | null>(
    null,
  );

  const propose = async () => {
    setBusy(true);
    setError(null);
    try {
      setResult(await gameKbApi.proposeConcepts(runId));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card" style={{ marginBottom: 16 }}>
      <div className="row" style={{ alignItems: 'center', gap: 8 }}>
        <div>
          <h2 style={{ margin: 0 }}>Propose concepts</h2>
          <p className="muted small" style={{ margin: '2px 0 0' }}>
            Scan this game for slot concepts / studio brandings missing from the catalogue and draft
            them for approval in <Link to="/settings/ontology">Ontology</Link>.
          </p>
        </div>
        <span className="spacer" />
        <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void propose()}>
          {busy ? 'Scanning…' : 'Propose from this game'}
        </button>
      </div>
      {error && <p className="small" style={{ color: 'var(--bad)', margin: '8px 0 0' }}>{error}</p>}
      {result && (
        <div className="small" style={{ marginTop: 10 }}>
          <p style={{ margin: 0 }} className="good">
            {result.createdDrafts.length} draft concept(s), {result.brandingsAdded.length} branding(s)
            added{result.skipped.length ? `, ${result.skipped.length} skipped (already known)` : ''}.
          </p>
          {result.createdDrafts.length > 0 && (
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {result.createdDrafts.map((c) => (
                <li key={c.slug}>
                  {c.canonicalName} <span className="muted mono">({c.slug})</span>
                </li>
              ))}
            </ul>
          )}
          {result.brandingsAdded.length > 0 && (
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {result.brandingsAdded.map((b) => (
                <li key={`${b.conceptSlug}:${b.text}`}>
                  branding <strong>{b.text}</strong> → <span className="mono">{b.conceptSlug}</span>
                </li>
              ))}
            </ul>
          )}
          {(result.createdDrafts.length > 0 || result.brandingsAdded.length > 0) && (
            <p style={{ margin: '6px 0 0' }}>
              Review and activate them in <Link to="/settings/ontology">Ontology</Link>.
            </p>
          )}
        </div>
      )}
    </section>
  );
}

function ProgressStrip({ progress }: { progress: GameKbStageProgress | undefined }) {
  if (!progress) return null;
  if (progress.status === 'completed') return null;
  if (progress.status === 'failed') {
    return (
      <p className="small" style={{ color: 'var(--bad)', margin: '6px 0 0' }}>
        Failed{progress.message ? `: ${progress.message}` : ''}
      </p>
    );
  }
  return (
    <div className="small muted" style={{ margin: '6px 0 0', display: 'flex', gap: 8, alignItems: 'center' }}>
      <span>{progress.tool ? `Tracing the package… ${progress.tool}` : progress.message || 'Working…'}</span>
    </div>
  );
}

function MetaLine({ meta }: { meta: GameKbArtifactMeta }) {
  if (meta.status === 'idle' && !meta.updatedAt) return null;
  const when = meta.updatedAt ? new Date(meta.updatedAt).toLocaleString() : null;
  return (
    <p className="small muted" style={{ margin: '4px 0 0' }}>
      {meta.status === 'error' && meta.error ? (
        <span style={{ color: 'var(--bad)' }}>Error: {meta.error}</span>
      ) : when ? (
        <>Generated {when}{meta.model ? ` · ${meta.model}` : ''}</>
      ) : null}
    </p>
  );
}

interface ArtifactCardProps {
  title: string;
  description: string;
  stage: GameKbStage;
  content: string | null;
  meta: GameKbArtifactMeta;
  progress: GameKbStageProgress | undefined;
  onGenerate: () => void;
  headerActions?: ReactNode;
  collapsibleSections?: boolean;
}

function ArtifactCard({ title, description, content, meta, progress, onGenerate, headerActions, collapsibleSections }: ArtifactCardProps) {
  const running = meta.status === 'running' || progress?.status === 'started' || progress?.status === 'progress';
  return (
    <section className="card" style={{ marginBottom: 16 }}>
      <div className="row" style={{ alignItems: 'center', gap: 8 }}>
        <div>
          <h2 style={{ margin: 0 }}>{title}</h2>
          <p className="muted small" style={{ margin: '2px 0 0' }}>{description}</p>
        </div>
        <span className="spacer" />
        {headerActions}
        <button
          type="button"
          className="btn btn-primary"
          disabled={running}
          onClick={onGenerate}
        >
          {running ? 'Generating…' : content ? 'Regenerate' : 'Generate'}
        </button>
      </div>
      <ProgressStrip progress={progress} />
      <MetaLine meta={meta} />
      {content ? (
        <div style={{ marginTop: 12 }}>
          {collapsibleSections ? <ReportView markdown={content} /> : <MarkdownView>{content}</MarkdownView>}
        </div>
      ) : (
        !running && (
          <p className="muted small" style={{ marginTop: 12 }}>
            Not generated yet.
          </p>
        )
      )}
    </section>
  );
}

/**
 * Extras "Game Knowledge" page: generate a short Game Description and a
 * detailed Full Report over the whole source package + screenshots + video,
 * and chat about the game grounded in those artifacts.
 */
export function GameKnowledgePage() {
  const { id } = useParams<{ id: string }>();
  const runId = id ?? '';
  const load = useGameKbStore((s) => s.load);
  const description = useGameKbStore((s) => s.description);
  const report = useGameKbStore((s) => s.report);
  const meta = useGameKbStore((s) => s.meta);
  const progress = useGameKbStore((s) => s.progress);
  const error = useGameKbStore((s) => s.error);
  const generateDescription = useGameKbStore((s) => s.generateDescription);
  const generateReport = useGameKbStore((s) => s.generateReport);

  useEffect(() => {
    if (runId) void load(runId);
  }, [runId, load]);

  return (
    <div className="game-kb-page">
      <header style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0 }}>6. Game Knowledge Base</h1>
        <p className="muted">
          A documented audit of this game. Generation reads the full extracted source package
          (not just critical files), the analysed screenshots, and the gameplay video. Pull the
          source code first (Phase 3) for best results.
        </p>
      </header>

      {error && <p style={{ color: 'var(--bad)' }}>{error}</p>}

      <ArtifactCard
        title="Game Description"
        description="Short 3-4 sentence summary of the core gameplay (excludes buy mechanics)."
        stage="description"
        content={description}
        meta={meta.description}
        progress={progress.description}
        onGenerate={() => void generateDescription()}
        headerActions={
          description ? <PostToJiraButton runId={runId} disabled={!description} /> : undefined
        }
      />

      <ArtifactCard
        title="Full Report"
        description="Detailed audit: theme, paytable & symbols, features, choices, pay direction, wilds/scatters, RTP variants, buy features and code-enforced limits."
        stage="report"
        content={report}
        meta={meta.report}
        progress={progress.report}
        onGenerate={() => void generateReport()}
        collapsibleSections
      />

      <ProposeConceptsCard runId={runId} />

      <section className="card">
        <GameChat runId={runId} />
      </section>
    </div>
  );
}
